import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { formatCurrency, formatInteger, formatRelativeTime } from './utils';

const BillingTab = ({
  pilotForm,
  onPilotFieldChange,
  onSavePilotSettings,
  savingPilot,
  billingForm,
  onBillingFieldChange,
  onPackFieldChange,
  onAddTokenPack,
  onRemoveTokenPack,
  onSaveBillingSettings,
  savingBilling,
  onResetSettings,
  creditInvoiceFilters,
  onCreditInvoiceFilterChange,
  onLoadCreditInvoices,
  creditInvoicesLoading,
  creditInvoiceCreateForm,
  onCreditInvoiceCreateFormChange,
  tenantSnapshot,
  onCreateCreditInvoice,
  creatingCreditInvoice,
  creditInvoiceAlert,
  onDismissCreditInvoiceAlert,
  creditInvoices,
  creditInvoiceActions,
  onDownloadCreditInvoicePdf,
  onSendCreditInvoice,
  onMarkCreditInvoicePaid,
}) => (
  <Stack spacing={3}>
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Pilot mode</Typography>
          <Typography variant="body2" color="text.secondary">
            Toggle between pilot and live behaviour for individual users.
          </Typography>
        </Box>
        <FormControlLabel
          control={(
            <Switch
              checked={Boolean(pilotForm.enabled)}
              onChange={onPilotFieldChange('enabled')}
            />
          )}
          label={pilotForm.enabled ? 'Pilot mode active' : 'Live mode active'}
        />
        <Typography variant="body2" color="text.secondary">
          Pilot mode hides Google/Microsoft OAuth buttons on login/signup and hides individual billing UI.
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSavePilotSettings}
            disabled={savingPilot}
          >
            {savingPilot ? 'Saving...' : 'Save pilot mode'}
          </Button>
          <Button variant="outlined" onClick={onResetSettings} disabled={savingPilot}>Reset</Button>
        </Stack>
      </Stack>
    </Paper>

    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Wallet defaults</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Introductory cases"
            type="number"
            fullWidth
            value={billingForm.freeSignupTokens}
            onChange={onBillingFieldChange('freeSignupTokens')}
            helperText="Non-purchasable case access granted at sign-up; never added to a paid wallet"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Tokens per history"
            type="number"
            fullWidth
            value={billingForm.historyCostTokens}
            onChange={onBillingFieldChange('historyCostTokens')}
            helperText="Consumed when a history is submitted"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="User registration cap"
            type="number"
            fullWidth
            value={billingForm.userRegistrationCap}
            onChange={onBillingFieldChange('userRegistrationCap')}
            helperText="Set to 0 for unlimited"
          />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={4} sx={{ mt: 2 }}>
        <FormControlLabel
          control={(
            <Switch
              checked={Boolean(billingForm.receiptsEnabled)}
              onChange={onBillingFieldChange('receiptsEnabled')}
            />
          )}
          label="Stripe receipts enabled"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={Boolean(billingForm.vatInvoicesEnabled)}
              onChange={onBillingFieldChange('vatInvoicesEnabled')}
            />
          )}
          label="VAT invoices enabled"
        />
      </Stack>
      <Stack spacing={2} sx={{ mt: 2 }}>
        <TextField
          label="Refund policy"
          multiline
          minRows={3}
          value={billingForm.refundPolicy}
          onChange={onBillingFieldChange('refundPolicy')}
        />
        <TextField
          label="Refund window (days)"
          type="number"
          value={billingForm.refundWindowDays}
          onChange={onBillingFieldChange('refundWindowDays')}
          sx={{ maxWidth: 240 }}
        />
      </Stack>
    </Paper>

    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">PlayStation wallet and tenant policy</Typography>
          <Typography variant="body2" color="text.secondary">
            Platform isolation is the production-safe default. The shared mode and university-sponsored PS5 access are rejected by the API until a Sony approval reference and approval date are recorded.
          </Typography>
        </Box>
        <Alert severity={billingForm.ps5WalletMode === 'sony-approved-shared' ? 'warning' : 'info'}>
          {billingForm.ps5WalletMode === 'sony-approved-shared'
            ? 'Sony-approved shared mode exposes paid balances across PlayStation and non-PlayStation platforms. Enable it only when the recorded approval explicitly permits this exact flow.'
            : 'Platform-isolated mode: PS5 can spend PlayStation Store credits only; web and mobile cannot spend those credits.'}
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="PS5 paid-credit policy"
              value={billingForm.ps5WalletMode}
              onChange={onBillingFieldChange('ps5WalletMode')}
            >
              <MenuItem value="platform-isolated">Platform isolated</MenuItem>
              <MenuItem value="sony-approved-shared">Sony-approved shared wallet</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Sony approval reference"
              value={billingForm.ps5SonyApprovalReference}
              onChange={onBillingFieldChange('ps5SonyApprovalReference')}
              helperText="Private Support or DevNet ticket/reference"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Sony approval date"
              value={billingForm.ps5SonyApprovedAt}
              onChange={onBillingFieldChange('ps5SonyApprovedAt')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="University-sponsored PS5 access"
              value={billingForm.ps5TenantAccessMode}
              onChange={onBillingFieldChange('ps5TenantAccessMode')}
            >
              <MenuItem value="blocked">Blocked pending approval</MenuItem>
              <MenuItem value="sony-approved">Sony approved</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Tenant-access approval reference"
              value={billingForm.ps5TenantApprovalReference}
              onChange={onBillingFieldChange('ps5TenantApprovalReference')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Tenant-access approval date"
              value={billingForm.ps5TenantApprovedAt}
              onChange={onBillingFieldChange('ps5TenantApprovedAt')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Stack>
    </Paper>

    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Token packs</Typography>
        <Button variant="outlined" onClick={onAddTokenPack}>Add pack</Button>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1480 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Tokens</TableCell>
            <TableCell>Price (GBP)</TableCell>
            <TableCell>Histories</TableCell>
            <TableCell>Stripe price ID</TableCell>
            <TableCell>Apple product ID</TableCell>
            <TableCell>Google product ID</TableCell>
            <TableCell>PSN product ID</TableCell>
            <TableCell>PSN entitlement label</TableCell>
            <TableCell>Active</TableCell>
            <TableCell width={80}><span className="sr-only">Actions</span></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {billingForm.tokenPacks.map((pack, index) => (
            <TableRow key={pack.key || index}>
              <TableCell>
                <TextField label="Pack name" value={pack.name} onChange={onPackFieldChange(index, 'name')} fullWidth />
              </TableCell>
              <TableCell>
                <TextField label="Tokens" type="number" value={pack.tokens} onChange={onPackFieldChange(index, 'tokens')} fullWidth />
              </TableCell>
              <TableCell>
                <TextField label="Price (GBP)" type="number" value={pack.price} onChange={onPackFieldChange(index, 'price')} fullWidth />
              </TableCell>
              <TableCell>
                <TextField label="Histories" type="number" value={pack.historiesEquivalent} onChange={onPackFieldChange(index, 'historiesEquivalent')} fullWidth />
              </TableCell>
              <TableCell>
                <TextField label="Stripe price ID" value={pack.stripePriceId} onChange={onPackFieldChange(index, 'stripePriceId')} fullWidth />
              </TableCell>
              <TableCell>
                <TextField label="Apple product ID" value={pack.appleProductId || ''} onChange={onPackFieldChange(index, 'appleProductId')} fullWidth />
              </TableCell>
              <TableCell>
                <TextField label="Google product ID" value={pack.googleProductId || ''} onChange={onPackFieldChange(index, 'googleProductId')} fullWidth />
              </TableCell>
              <TableCell>
                <TextField label="PSN product ID" value={pack.psnProductId || ''} onChange={onPackFieldChange(index, 'psnProductId')} fullWidth />
              </TableCell>
              <TableCell>
                <TextField label="PSN entitlement label" value={pack.psnEntitlementLabel || ''} onChange={onPackFieldChange(index, 'psnEntitlementLabel')} fullWidth />
              </TableCell>
              <TableCell>
                <FormControlLabel
                  control={<Switch checked={Boolean(pack.isActive)} onChange={onPackFieldChange(index, 'isActive')} />}
                  label="Enabled"
                />
              </TableCell>
              <TableCell>
                <Button color="error" onClick={() => onRemoveTokenPack(index)}>Remove</Button>
              </TableCell>
            </TableRow>
          ))}
          {!billingForm.tokenPacks.length && (
            <TableRow>
              <TableCell colSpan={11} align="center">
                <Typography variant="body2" color="text.secondary">
                  No token packs defined. Add a pack to begin selling tokens.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </Box>
    </Paper>

    <Stack direction="row" spacing={2}>
      <Button variant="contained" color="primary" onClick={onSaveBillingSettings} disabled={savingBilling}>
        {savingBilling ? 'Saving...' : 'Save billing settings'}
      </Button>
      <Button variant="outlined" onClick={onResetSettings}>Reset</Button>
    </Stack>

    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
          <Typography variant="h6">Credit invoices</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              select
              label="Status"
              value={creditInvoiceFilters.status}
              onChange={onCreditInvoiceFilterChange('status')}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={onLoadCreditInvoices} disabled={creditInvoicesLoading}>
              {creditInvoicesLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              select
              label="University"
              value={creditInvoiceCreateForm.universityId}
              onChange={(event) => onCreditInvoiceCreateFormChange((prev) => ({ ...prev, universityId: event.target.value }))}
              fullWidth
            >
              <MenuItem value=""><em>Select a university</em></MenuItem>
              {(tenantSnapshot?.tenants || []).map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>{tenant.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Credits"
              type="number"
              value={creditInvoiceCreateForm.quantity}
              onChange={(event) => onCreditInvoiceCreateFormChange((prev) => ({ ...prev, quantity: event.target.value }))}
              inputProps={{ min: 1 }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button variant="contained" onClick={onCreateCreditInvoice} disabled={creatingCreditInvoice} fullWidth>
              {creatingCreditInvoice ? 'Creating...' : 'Create draft invoice'}
            </Button>
          </Grid>
        </Grid>

        <Divider />

        {creditInvoiceAlert && (
          <Alert severity={creditInvoiceAlert.severity} onClose={onDismissCreditInvoiceAlert}>
            {creditInvoiceAlert.message}
          </Alert>
        )}

        {creditInvoicesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : creditInvoices.length === 0 ? (
          <Alert severity="info">No credit invoices found.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>University</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {creditInvoices.map((invoice) => {
                const isSending = creditInvoiceActions.sending === invoice.id;
                const isMarkingPaid = creditInvoiceActions.markingPaid === invoice.id;
                const isDownloading = creditInvoiceActions.downloading === invoice.id;
                const canSend = invoice.status === 'draft';
                const canMarkPaid = invoice.status !== 'paid';
                return (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.universityName || invoice.universityId}</TableCell>
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{invoice.status}</TableCell>
                    <TableCell>{formatInteger(invoice.quantity)}</TableCell>
                    <TableCell>{formatCurrency(invoice.totalCents, invoice.currency)}</TableCell>
                    <TableCell>{formatRelativeTime(invoice.requestedAt || invoice.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {invoice.stripeHostedInvoiceUrl && (
                          <Button size="small" component="a" href={invoice.stripeHostedInvoiceUrl} target="_blank" rel="noreferrer">
                            Stripe
                          </Button>
                        )}
                        <Button size="small" variant="outlined" onClick={() => onDownloadCreditInvoicePdf(invoice)} disabled={isDownloading}>
                          {isDownloading ? 'Downloading...' : 'PDF'}
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => onSendCreditInvoice(invoice.id)} disabled={!canSend || isSending}>
                          {isSending ? 'Sending...' : 'Send invoice'}
                        </Button>
                        <Button size="small" variant="contained" onClick={() => onMarkCreditInvoicePaid(invoice.id)} disabled={!canMarkPaid || isMarkingPaid}>
                          {isMarkingPaid ? 'Marking...' : 'Mark paid'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Stack>
    </Paper>
  </Stack>
);

export default BillingTab;
